/*
 * Copyright (c) 2023 - 2026. Bolmate B.V.
 */
import './style/testMail.scss';

import React, {FocusEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {AwesomeIcon, ChangeFunction, ErrorType, Loader} from '@dvrd/dvr-controls';

import {BolmateDialog, BolmateInput} from '../../controls';

import EmailDisabledMessage from '../../misc/emailDisabledMessage';

import {PushDevice} from '../../../util/interfaces';

import {deviceDisplayName, deviceTypeIcon} from '../../../util/pushChannelUtil';
import {hasAdminLogin} from '../../../util/adminUtil.ts';

interface Props {
    onChange: ChangeFunction<string>;
    onSubmit: VoidFunction;
    onClose: VoidFunction;
    onResetError: FocusEventHandler;
    error: ErrorType;
    active: boolean;
    email: string;
    loading: boolean;
    pushDevices?: Array<PushDevice>;
    /** False when the notification's EMAIL channel is off — the e-mail section
     *  is hidden then and only the push targets are shown. Defaults to true. */
    showEmail?: boolean;
    /** Optional dialog title — callers (e.g. the notifications view) pass a
     *  context-specific title; defaults to the generic test-mail title. */
    title?: string;
}

export default function TestMail(props: Props) {
    const {t} = useTranslation();
    const {onClose, loading, error, onChange, active, email, onSubmit, onResetError, pushDevices, title} = props;
    const showEmail = props.showEmail !== false;
    const deviceLabels = {
        installedApp: t('push.installedApp', 'geïnstalleerde app'),
        unknownDevice: t('push.unknownDevice', 'Onbekend apparaat'),
        thisDevice: {
            mobile: t('push.thisPhone', 'Deze telefoon'),
            tablet: t('push.thisTablet', 'Deze tablet'),
            desktop: t('push.thisComputer', 'Deze computer')
        },
        formFactor: {
            mobile: t('push.formFactorPhone', 'Telefoon'),
            tablet: t('push.formFactorTablet', 'Tablet'),
            desktop: t('push.formFactorComputer', 'Computer')
        }
    };

    return (
        <BolmateDialog
            active={active}
            onClose={onClose}
            title={title ?? t('email.test.title', 'Test mail verzenden')}
            className='test-mail-popup'
            onConfirm={onSubmit}
            confirmLabel={t('email.test.send', 'Verzenden')}
            cancelLabel={t('email.test.cancel', 'Annuleren')}
            confirmDisabled={showEmail && !email.length}
            loading={loading}
        >
            <Loader active={loading} />
            {showEmail && (
                <section className='test-mail-section'>
                    <div className='test-mail-section-head'>
                        <AwesomeIcon name='envelope' />
                        <span className='test-mail-section-title'>{t('email.test.emailSection', 'E-mail')}</span>
                    </div>
                    <span className='test-mail-section-hint'>
                        {t('email.test.sendTo', 'De test e-mail wordt verzonden naar:')}
                    </span>
                    <BolmateInput
                        onChange={onChange}
                        value={email}
                        inputProps={{type: 'email'}}
                        onEnter={onSubmit}
                        error={error}
                        onFocus={onResetError}
                        ornaments={{
                            element: (
                                <EmailDisabledMessage>
                                    {t(
                                        'email.test.cannotChange',
                                        'Vanwege veiligheid is het niet mogelijk dit e-mailadres aan te passen.'
                                    )}
                                </EmailDisabledMessage>
                            )
                        }}
                        autoFocus
                        disabled={!hasAdminLogin()}
                    />
                </section>
            )}
            {pushDevices && (
                <>
                    {showEmail && <div className='test-mail-divider' />}
                    <section className='test-mail-section'>
                        <div className='test-mail-section-head'>
                            <AwesomeIcon name='bell' />
                            <span className='test-mail-section-title'>
                                {t('email.test.pushSection', 'Push-melding')}
                            </span>
                        </div>
                        <span className='test-mail-section-hint'>
                            {t('email.test.pushSendTo', 'Push-melding wordt verzonden naar:')}
                        </span>
                        {pushDevices.length > 0 ? (
                            <ul className='push-device-list'>
                                {pushDevices.map(device => (
                                    <li key={device.id}>
                                        <AwesomeIcon name={deviceTypeIcon(device)} />
                                        <span>{deviceDisplayName(device, deviceLabels)}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <span className='no-devices'>{t('push.devicesNone', 'Geen apparaten geregistreerd')}</span>
                        )}
                    </section>
                </>
            )}
        </BolmateDialog>
    );
}
